import {
  EMPTY_ENTERPRISE_AGGREGATION_REQUEST,
  EnterpriseAggregationRequest,
  EnterpriseSearchFilterType,
  KeywordAutocompleteOption,
} from 'types/supplierSearch';
import * as Yup from 'yup';
import {
  CERTIFICATION_OPTIONS,
  ENTERPRISE_LOCATION_OPTIONS,
  viewStateFormSchema,
} from 'views/sourcing/SupplierSearch/constants';
import _ from 'lodash';
import { useState, useMemo, useEffect } from 'react';
import toMaterialStyle from 'material-color-hash';

export function transformViewStateToRequest(
  viewState: Yup.InferType<typeof viewStateFormSchema>
): EnterpriseAggregationRequest {
  const activeLocations =
    viewState.locationFilters?.filter((filter) => filter.active) ?? [];
  const activeCertifications =
    viewState.certificationFilters?.filter((filter) => filter.active) ?? [];
  return {
    ...EMPTY_ENTERPRISE_AGGREGATION_REQUEST,
    keywordFilters:
      viewState.keywordFilters
        ?.filter((filter) => filter.active)
        .map((filter) => ({
          query: filter.query,
          appearance:
            filter.appearance as KeywordAutocompleteOption['appearance'],
        })) ?? [],
    enterpriseClassFilter:
      viewState.enterpriseClassFilters
        ?.filter((filter) => filter.active)
        .map((filter) => filter.value) ?? [],
    ontComponentFilters: {
      ...EMPTY_ENTERPRISE_AGGREGATION_REQUEST.ontComponentFilters,
      [CERTIFICATION_OPTIONS.ontPropertyComponentId]: {
        ontPropertyComponentId: CERTIFICATION_OPTIONS.ontPropertyComponentId,
        type: EnterpriseSearchFilterType.ENUMERATION,
        ...(activeCertifications.length > 0
          ? {
              filter: {
                values: activeCertifications.map((filter) => filter.value),
              },
            }
          : undefined),
      },
      [ENTERPRISE_LOCATION_OPTIONS.ontPropertyComponentId]: {
        ontPropertyComponentId:
          ENTERPRISE_LOCATION_OPTIONS.ontPropertyComponentId,
        type: EnterpriseSearchFilterType.LOCATION,
        ...(activeLocations.length > 0
          ? {
              filter: {
                values: activeLocations.map((filter) => filter.value),
              },
            }
          : undefined),
      },
    },
  };
}

export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const debouncedSet = useMemo(
    () =>
      _.debounce((updatedValue: T) => {
        setDebouncedValue(_.cloneDeep(updatedValue));
      }, delay),
    [delay]
  );
  useEffect(() => {
    debouncedSet(value);
  }, [value, debouncedSet]);
  return debouncedValue;
}

export enum CogbaseAttachmentType {
  DOWNLOAD = 'download',
  THUMBNAIL = 'thumbnail',
  THUMBNAIL_CROPPED = 'thumbnail-cropped',
  PREVIEW = 'preview',
}

export function getAttachmentApiPath(
  id: string,
  type: CogbaseAttachmentType,
  filename?: string
) {
  const baseUrl = process.env.COGBASE_API_URL ?? 'https://cogbase.com';
  if (filename) {
    return `${baseUrl}/api/attachments/${type}/${id}/${encodeURIComponent(
      filename
    )}`;
  }
  return `${baseUrl}/api/attachments/${type}/${id}`;
}

export function getDefaultEnterpriseLogoColor(enterpriseId: string) {
  return toMaterialStyle(enterpriseId.toLowerCase(), 500);
}

export function getAbbreviation(name: string) {
  const words = name
    .replaceAll(/[^\d a-z]/gi, '')
    .split(' ')
    .filter((word) => word.length > 0);
  if (words.length === 0) {
    return '';
  }
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return words[0].charAt(0).toUpperCase() + words[1].charAt(0).toUpperCase();
}

export const getMatchSuccessColor = (percentMatch: number) => {
  if (percentMatch >= 0.9) {
    return 'success.main';
  }
  if (percentMatch >= 0.6) {
    return 'success.light';
  }
  if (percentMatch >= 0.3) {
    return 'warning.main';
  }
  return 'error.main';
};
